import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaTimes, FaPlus } from 'react-icons/fa';
import { db } from '../../pages/app/protected/firebase';
import { collection, doc, addDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { storage } from '../../pages/app/protected/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../contexts/AuthContext';

const ANY_VARIANT_ID = '__ANY_VARIANT__';

export default function AddBundle({ isOpen, onClose, products = [], bundle = null }) {
  const { user, companyId } = useAuth();
  const [bundleName, setBundleName] = useState('');
  const [bundleSku, setBundleSku] = useState('');
  const [bundlePrice, setBundlePrice] = useState('');
  const [bundleItems, setBundleItems] = useState([]);
  const [bundleQuantity, setBundleQuantity] = useState(0);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [bundleTotal, setBundleTotal] = useState(0);
  const [totalProductPurchase, setTotalProductPurchase] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [onlineOrdering, setOnlineOrdering] = useState(true);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  // Initialize form when bundle prop changes
  useEffect(() => {
    if (bundle) {
      setBundleName(bundle.name || '');
      setBundleSku(bundle.sku || '');
      setBundlePrice(bundle.price?.toString() || '');
      setOnlineOrdering(bundle.onlineOrdering !== undefined ? bundle.onlineOrdering : true);
      const imgs = Array.isArray(bundle.images) ? bundle.images : bundle.image ? [bundle.image] : [];
      setExistingImages(imgs);
      setImagePreviews(imgs);
      if (bundle.items?.length) {
        setBundleItems(bundle.items.map(it => ({
          productId: it.id,
          variantId: it.variantId || '',
          allowVariantChoice: Boolean(it.allowVariantChoice) || it.variantId === ANY_VARIANT_ID,
          quantity: it.quantity || 1,
        })));
      } else {
        setBundleItems([]);
      }
    } else {
      resetForm();
    }
  }, [bundle]);

  // Update calculations when bundle items or products change
  useEffect(() => {
    updateCalculations();
  }, [bundleItems, products]);

  // Normalize bundleItems to array to avoid legacy object shape
  useEffect(() => {
    if (!Array.isArray(bundleItems)) {
      setBundleItems([]);
    }
  }, [bundleItems]);

  const updateCalculations = useCallback(() => {
    const itemsArr = Array.isArray(bundleItems) ? bundleItems : [];
    const getTotalVariantQty = (product) =>
      Array.isArray(product?.variants)
        ? product.variants.reduce((sum, v) => sum + Number(v.qty || 0), 0)
        : 0;

    // Update bundle total
    const total = itemsArr.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      const basePrice = product?.price ?? 0;
      const variant = product?.variants?.find(v => v.id === item.variantId);
      const variantPrice = variant?.price ?? basePrice;
      const isAnyVariant = item.variantId === ANY_VARIANT_ID || item.allowVariantChoice;
      const price = isAnyVariant ? basePrice : variantPrice;
      return sum + price * item.quantity;
    }, 0);
    setBundleTotal(total);

    // update purcahse price

    const productPurchase = itemsArr.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      const variant = product?.variants?.find(v => v.id === item.variantId);
      const isAnyVariant = item.variantId === ANY_VARIANT_ID || item.allowVariantChoice;
      const cost = isAnyVariant
        ? product?.purchase ?? 0
        : variant?.purchase ?? product?.purchase ?? 0;
      return sum + cost * item.quantity;
    }, 0);
    setTotalProductPurchase(productPurchase);

    // Update available quantity
    const quantities = itemsArr
      .filter((item) => item.quantity > 0)
      .map((item) => {
        const product = products.find(p => p.id === item.productId);
        const variant = product?.variants?.find(v => v.id === item.variantId);
        const totalVariantQty = getTotalVariantQty(product);
        const availableBase = typeof product?.qty === 'number' ? product.qty : totalVariantQty;
        const isAnyVariant = item.variantId === ANY_VARIANT_ID || item.allowVariantChoice;
        const available = isAnyVariant
          ? availableBase
          : variant
            ? variant.qty
            : availableBase;
        return available ? Math.floor(available / item.quantity) : 0;
      });
    
    const minQuantity = quantities.length > 0 ? Math.min(...quantities) : 0;
    setBundleQuantity(Number.isFinite(minQuantity) ? minQuantity : 0);
  }, [bundleItems, products]);

  const handleAddProduct = () => {
    if (!selectedProductId || selectedQuantity < 1) return;
    const key = `${selectedProductId}::${selectedVariantId || 'base'}`;
    setBundleItems((prev) => {
      const existingIdx = prev.findIndex(
        (i) => `${i.productId}::${i.variantId || 'base'}` === key
      );
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = {
          ...copy[existingIdx],
          quantity: copy[existingIdx].quantity + selectedQuantity,
        };
        return copy;
      }
      return [
        ...prev,
        {
          productId: selectedProductId,
          variantId: selectedVariantId,
          quantity: selectedQuantity,
          allowVariantChoice: selectedVariantId === ANY_VARIANT_ID,
        },
      ];
    });
    setSelectedProductId('');
    setSelectedVariantId('');
    setSelectedQuantity(1);
  };

  const uploadNewImages = async () => {
    if (!newImages.length || !companyId) return [];
    const uploads = newImages.map(async (file) => {
      const imageRef = ref(storage, `companies/${companyId}/bundleUploads/${uuidv4()}`);
      await uploadBytes(imageRef, file);
      return await getDownloadURL(imageRef);
    });
    return Promise.all(uploads);
  };

  const handleSubmit = async () => {
    if (!bundleName.trim() || !bundlePrice || !bundleSku.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if SKU already exists (only for new bundles or when SKU is changed)
    if (!bundle || bundle.sku !== bundleSku.trim()) {
      const skuExists = await checkIfSkuExists(bundleSku.trim());
      if (skuExists) {
        setError('A bundle with this SKU already exists');
        return;
      }
    }

    // Check if name already exists (only for new bundles or when name is changed)
    if (!bundle || bundle.name !== bundleName.trim()) {
      const nameExists = await checkIfNameExists(bundleName.trim());
      if (nameExists) {
        setError('A bundle with this name already exists');
        return;
      }
    }

    const selectedItems = bundleItems
      .filter((it) => it.quantity > 0)
      .map((it) => {
        const product = products.find(p => p.id === it.productId);
        const variant = product?.variants?.find(v => v.id === it.variantId);
        const basePrice = parseFloat(product?.price ?? variant?.price ?? 0);
        return {
          id: it.productId,
          variantId: it.variantId || '',
          name: product?.name || 'Unknown Product',
          variantName: it.variantId === ANY_VARIANT_ID
            ? 'Any variant'
            : variant
              ? variant.options?.map(o => `${o.group}: ${o.value}`).join(' / ')
              : '',
          quantity: parseInt(it.quantity, 10),
          sku: variant?.sku || product?.sku || '',
          price: parseFloat(it.variantId === ANY_VARIANT_ID ? basePrice : variant?.price ?? product?.price ?? 0),
          basePrice,
          allowVariantChoice: it.variantId === ANY_VARIANT_ID,
        };
      });

    if (selectedItems.length === 0) {
      setError('Please add at least one product to the bundle');
      return;
    }

    // Calculate total cost of items
    const totalValue = selectedItems.reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );
    
    // Calculate bundle loss (total cost - bundle price)
    const bundleLoss = Math.max(0, totalValue - parseFloat(bundlePrice));

    try {
      setLoading(true);
      setError('');
      const uploadedImages = await uploadNewImages();
      const finalImages = [...existingImages, ...uploadedImages];
      const imageUrl = finalImages[0] || null;
      
      const bundleData = {
        name: bundleName.trim(),
        sku: bundleSku.trim(),
        price: parseFloat(bundlePrice),
        items: selectedItems,
        quantity: bundleQuantity,
        type: 'Bundle',
        totalProductPurchase: parseFloat(totalProductPurchase.toFixed(2)),
        totalValue: parseFloat(totalValue.toFixed(2)),
        bundleLoss: parseFloat(bundleLoss.toFixed(2)),
        onlineOrdering: onlineOrdering,
        updatedAt: new Date().toISOString(),
        image: imageUrl,
        images: finalImages,
      };

      if (bundle?.id) {
        await updateDoc(
          doc(db, 'companies', companyId, 'inventory', 'data', 'bundles', bundle.id),
          bundleData
        );
      } else {
        bundleData.createdAt = new Date().toISOString();
        await addDoc(
          collection(db, 'companies', companyId, 'inventory', 'data', 'bundles'),
          bundleData
        );
      }

      resetForm();
      onClose();
    } catch (err) {
      console.error('Error saving bundle:', err);
      setError('Failed to save bundle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if SKU already exists in the database
  const checkIfSkuExists = async (sku) => {
    try {
      const bundlesRef = collection(db, 'companies', companyId, 'inventory', 'data', 'bundles');
      const q = query(bundlesRef, where('sku', '==', sku));
      const querySnapshot = await getDocs(q);
      
      // If editing, exclude the current bundle from the check
      if (bundle?.id) {
        return querySnapshot.docs.some(doc => doc.id !== bundle.id);
      }
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking SKU:', error);
      return false;
    }
  };

  // Check if name already exists in the database
  const checkIfNameExists = async (name) => {
    try {
      const bundlesRef = collection(db, 'companies', companyId, 'inventory', 'data', 'bundles');
      const q = query(bundlesRef, where('name', '==', name));
      const querySnapshot = await getDocs(q);
      
      // If editing, exclude the current bundle from the check
      if (bundle?.id) {
        return querySnapshot.docs.some(doc => doc.id !== bundle.id);
      }
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking name:', error);
      return false;
    }
  };

  const resetForm = () => {
    setBundleName('');
    setBundleSku('');
    setBundlePrice('');
    setBundleItems([]);
    setBundleQuantity(0);
    setSelectedProductId('');
    setSelectedVariantId('');
    setSelectedQuantity(1);
    setBundleTotal(0);
    setError('');
    setExistingImages([]);
    setNewImages([]);
    setImagePreviews([]);
  };

  if (!isOpen) return null;

  const availableProducts = products;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {bundle ? 'Edit' : 'Create New'} Bundle/Set
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 text-red-300 rounded text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                SKU <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={bundleSku}
                onChange={(e) => !bundle && setBundleSku(e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-white ${
                  bundle ? 'bg-gray-700 border-gray-600 cursor-not-allowed' : 'bg-gray-800 border-gray-700'
                }`}
                placeholder="BNDL-001"
                required
                disabled={Boolean(bundle)}
                readOnly={Boolean(bundle)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Bundle Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={bundleName}
                onChange={(e) => setBundleName(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                placeholder="Combo Meal 1"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Bundle Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2">RM</span>
                <input
                  type="number"
                  value={bundlePrice}
                  onChange={(e) => setBundlePrice(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-md pl-10 pr-3 py-2 text-white"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>
            <div className="bg-gray-800 p-3 rounded-md border border-gray-700">
              <div className="text-sm text-gray-300">Available Quantity</div>
              <div className="text-2xl font-bold text-white">{bundleQuantity}</div>
              <div className="text-xs text-gray-400 mt-1">
                Based on lowest stock item
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Add Products <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Select products and quantities to include in this bundle
            </p>
            <div className="bg-gray-800 border border-gray-700 rounded-md p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_auto_auto] gap-2">
                <select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="">Select a product</option>
                  {availableProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} (RM{product.price} each, {product.qty} in stock)
                    </option>
                  ))}
                </select>
                {selectedProductId && (products.find(p => p.id === selectedProductId)?.variants?.length > 0) && (
                  <select
                    value={selectedVariantId}
        onChange={(e) => setSelectedVariantId(e.target.value)}
        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
      >
        <option value="">Select variant</option>
        <option value={ANY_VARIANT_ID}>Any variant (choose at checkout)</option>
                    {products.find(p => p.id === selectedProductId)?.variants?.map(v => {
                      const base = products.find(p => p.id === selectedProductId);
                      const variantPrice = v.price ?? base?.price ?? 0;
                      return (
                        <option key={v.id} value={v.id}>
                          {(v.options || []).map(o => `${o.group}: ${o.value}`).join(' / ')} | Qty: {v.qty ?? 0} | RM{variantPrice.toFixed(2)}
                        </option>
                      );
                    })}
                  </select>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-2 text-white text-center"
                  />
                  <button
                    onClick={handleAddProduct}
                    disabled={!selectedProductId || (products.find(p => p.id === selectedProductId)?.variants?.length > 0 && !selectedVariantId)}
                    className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </div>

              {bundleItems.filter(it => it.quantity > 0).length > 0 ? (
                <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700">
                        <th className="pb-2">Product</th>
                        <th className="pb-2 text-right">Qty</th>
                        <th className="pb-2 text-right">Price</th>
                        <th className="pb-2 text-right">Total</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bundleItems
                        .filter((it) => it.quantity > 0)
                        .map((it, idx) => {
                          const product = products.find(p => p.id === it.productId);
                          if (!product) return null;
                          const variant = product.variants?.find(v => v.id === it.variantId);
                          const basePrice = product.price ?? 0;
                          const price = it.variantId === ANY_VARIANT_ID ? basePrice : variant?.price ?? basePrice;
                          const maxQty = variant ? variant.qty : product.qty;
                          const itemTotal = price * it.quantity;
                          return (
                            <tr key={`${it.productId}-${it.variantId || 'base'}-${idx}`} className="border-b border-gray-700">
                              <td className="py-2">
                                <div>{product.name}</div>
                                {it.variantId === ANY_VARIANT_ID && (
                                  <div className="text-[11px] text-gray-400">Any variant (choose at checkout)</div>
                                )}
                                {variant && <div className="text-[11px] text-gray-400">{(variant.options || []).map(o => `${o.group}: ${o.value}`).join(' / ')}</div>}
                              </td>
                              <td className="text-right">
                                <input
                                  type="number"
                                  min="1"
                                  max={maxQty}
                                  value={it.quantity}
                                  onChange={(e) => {
                                    const newQty = Math.max(1, Math.min(parseInt(e.target.value) || 1, maxQty || Infinity));
                                    setBundleItems(prev => prev.map((entry, i) => i === idx ? { ...entry, quantity: newQty } : entry));
                                  }}
                                  className="w-16 bg-gray-700 border border-gray-600 rounded px-1 py-1 text-white text-right"
                                />
                              </td>
                              <td className="text-right">RM {parseFloat(price).toFixed(2)}</td>
                              <td className="text-right">RM {itemTotal.toFixed(2)}</td>
                              <td className="text-right">
                                <button
                                  onClick={() => {
                                    setBundleItems(prev => prev.filter((_, i) => i !== idx));
                                  }}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-sm py-2 text-center">
                  No products added yet
                </p>
              )}
            </div>
          </div>

          <div className="bg-gray-800 p-3 rounded-md border border-gray-700 space-y-2">
            <label className="text-sm font-medium text-gray-300">Bundle Images</label>
            <div
              className="border border-dashed border-gray-500 rounded-lg p-4 text-center text-gray-300 cursor-pointer hover:border-purple-500"
              onClick={() => fileInputRef.current?.click()}
            >
              <p className="font-medium">Click to upload</p>
              <p className="text-xs text-gray-400">PNG, JPG (max 2MB) · You can select multiple</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setNewImages((prev) => [...prev, ...files]);
                const previews = files.map((file) => URL.createObjectURL(file));
                setImagePreviews((prev) => [...prev, ...previews]);
              }}
            />
            {imagePreviews.length > 0 && (
              <div className="flex gap-3 overflow-x-auto">
                {imagePreviews.map((src, idx) => (
                  <div key={idx} className="relative">
                    <img src={src} alt={`Bundle ${idx}`} className="h-16 w-16 object-cover rounded border border-gray-700" />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full h-5 w-5 text-[10px]"
                      onClick={() => {
                        const existingCount = existingImages.length;
                        if (idx < existingCount) {
                          setExistingImages((prev) => prev.filter((_, i) => i !== idx));
                        } else {
                          const newIdx = idx - existingCount;
                          setNewImages((prev) => prev.filter((_, i) => i !== newIdx));
                        }
                        setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Bundle Summary</h4>
            <div className="bg-gray-800 p-3 rounded-md border border-gray-700">
              {bundleItems.filter(it => it.quantity > 0).length > 0 ? (
                <>
                  <ul className="space-y-2 mb-4">
                    {bundleItems
                      .filter((it) => it.quantity > 0)
                      .map((it, idx) => {
                        const product = products.find(p => p.id === it.productId);
                        if (!product) return null;
                        const variant = product.variants?.find(v => v.id === it.variantId);
                        const basePrice = product.price ?? 0;
                        const price = it.variantId === ANY_VARIANT_ID ? basePrice : variant?.price ?? basePrice;
                        const itemTotal = price * it.quantity;
                        return (
                          <li key={`${it.productId}-${it.variantId || 'base'}-${idx}`} className="flex justify-between text-sm">
                            <span className="text-gray-300">
                              {product.name}{' '}
                              {it.variantId === ANY_VARIANT_ID
                                ? '(Any variant)'
                                : variant
                                  ? `(${(variant.options || []).map(o => `${o.group}: ${o.value}`).join(' / ')})`
                                  : ''} x{it.quantity}
                            </span>
                            <span className="text-gray-400">RM {itemTotal.toFixed(2)}</span>
                          </li>
                        );
                      })}
                  </ul>
                  <div className="border-t border-gray-700 pt-2">
                    <div className="flex justify-between font-medium">
                      <span>Total Items Value:</span>
                      <span>RM {bundleTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium mt-1">
                      <span>Bundle Price:</span>
                      <span>RM {parseFloat(bundlePrice || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-400 text-sm">No products added to bundle</p>
              )}
            </div>
          </div>

          {/* Online Ordering Toggle */}
          <div className="flex items-center justify-between bg-gray-800 p-4 rounded-lg mt-4">
            <div>
              <label className="text-sm font-medium text-white">Available for Online Ordering</label>
              <p className="text-xs text-gray-400">Allow customers to order this bundle online</p>
            </div>
            <button
              type="button"
              onClick={() => setOnlineOrdering(!onlineOrdering)}
              className={`w-12 h-6 flex items-center rounded-full p-1 duration-300 ${onlineOrdering ? 'bg-purple-600' : 'bg-gray-600'}`}
            >
              <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${onlineOrdering ? 'translate-x-6' : ''}`} />
            </button>
          </div>

          <div className="flex justify-between items-center pt-4">
            <button
              type="button"
              onClick={() => {
                onClose();
                resetForm();
              }}
              disabled={loading}
              className="px-4 py-2 border border-gray-600 rounded-md text-white hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || bundleItems.length === 0}
              className={`px-4 py-2 rounded-md text-white ${
                bundleItems.length > 0
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-gray-600 cursor-not-allowed'
              }`}
            >
              {loading ? 'Saving...' : bundle ? 'Update Bundle' : 'Create Bundle'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
